import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { DashboardPage } from './pages/DashboardPage';
import { PricingPage } from './pages/PricingPage';
import { PredictionDetailPage } from './pages/PredictionDetailPage';
import { PredictionsPage } from './pages/PricingsPage';
import { AuctionFilesPage } from './pages/FilesPage';
import { FileDetailPage } from './pages/FileDetailPage';
import { MappingsPage } from './pages/MappingsPage';
import MappingEditorPage from './pages/MappingEditorPage';
import { ActualDataSourcesPage } from './pages/ActualDataSourcesPage';
import { PredictionComparisonsPage } from './pages/PredictionComparisonsPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { CircularProgress, Box } from '@mui/material';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check both user state and token for authentication
  const token = localStorage.getItem('token');
  const authenticated = isAuthenticated || !!token;

  return authenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/auction-files"
              element={
                <PrivateRoute>
                  <AuctionFilesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/auction-files/:id"
              element={
                <PrivateRoute>
                  <FileDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/files"
              element={
                <PrivateRoute>
                  <AuctionFilesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/mappings"
              element={
                <PrivateRoute>
                  <MappingsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/mapping-editor"
              element={
                <PrivateRoute>
                  <MappingEditorPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pricing"
              element={
                <PrivateRoute>
                  <PricingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/predictions"
              element={
                <PrivateRoute>
                  <PredictionsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pricings"
              element={
                <PrivateRoute>
                  <PredictionsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/predictions/:id"
              element={
                <PrivateRoute>
                  <PredictionDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/actual-data-sources"
              element={
                <PrivateRoute>
                  <ActualDataSourcesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/prediction-comparisons"
              element={
                <PrivateRoute>
                  <PredictionComparisonsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <PrivateRoute>
                  <ChangePasswordPage />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;

